import React, {useEffect, useRef, useState} from 'react';
import DatePicker, {registerLocale} from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {format, isSameDay} from 'date-fns';
import {httpsCallable} from 'firebase/functions';
import {functions} from '../../../firebase';
import it from 'date-fns/locale/it';
import Loader from '../../commons/loader';
import MyNormalText from '../../commons/myNormalText';
import ItemBox from '../item/itemBox';
import MainButton from '../../commons/mainButton';
import {fromMomentDate, toMomentDate} from "../../../global/dates";
import {GLOBAL_BORDER_RADIUS} from "../../../costants";

registerLocale('it', it);

const ItemsAvailable = ({rentUser, premiumPalette}) => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [availableItems, setAvailableItems] = useState([]);
    const [sortedItems, setSortedItems] = useState([]);
    const [sortCriteria, setSortCriteria] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const lastPayload = useRef(null);

    const checkAvailability = async () => {
        if (!startDate) {
            setError('Seleziona almeno una data di inizio.');
            return;
        }
        if (isSameDay(startDate, endDate)) {
            if (!startTime || !endTime) {
                setError('Seleziona orario di inizio e fine per una prenotazione di un singolo giorno.');
                return;
            }
            if (startTime >= endTime) {
                setError("L'orario di fine deve essere successivo all'orario di inizio.");
                return;
            }
        }

        const request = {
            rentId: rentUser.rentId,
            startDate: fromMomentDate(toMomentDate(startDate)),
            endDate: endDate ? fromMomentDate(toMomentDate(endDate)) : null,
            startTime: isSameDay(startDate, endDate) || !endDate ? format(startTime, 'HH:mm') : null,
            endTime: isSameDay(startDate, endDate) || !endDate ? format(endTime, 'HH:mm') : null,
        };

        if (lastPayload.current && JSON.stringify(lastPayload.current) === JSON.stringify(request)) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const checkAvailabilityFunction = httpsCallable(functions, 'rentWebsiteCheckAvailabilityItemsView');
            const result = await checkAvailabilityFunction(request);
            setAvailableItems(result.data.items || []);
            lastPayload.current = request;
        } catch (err) {
            setError('Errore durante il recupero degli articoli disponibili.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const sortItems = (items, criteria, order) => {
        const sorted = [...items];
        sorted.sort((a, b) => {
            let valueA = a[criteria] || '';
            let valueB = b[criteria] || '';

            valueA = typeof valueA === 'string' ? valueA.toLowerCase() : valueA;
            valueB = typeof valueB === 'string' ? valueB.toLowerCase() : valueB;

            if (order === 'asc') {
                return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
            } else {
                return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
            }
        });
        return sorted;
    };

    const handleSort = (criteria) => {
        if (criteria === sortCriteria) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortCriteria(criteria);
            setSortOrder('asc');
        }
    };

    const setToday = () => {
        const today = new Date();
        setStartDate(today);
        setEndDate(null);
        setStartTime(null);
        setEndTime(null);
    };

    const setTomorrow = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setStartDate(tomorrow);
        setEndDate(null);
        setStartTime(null);
        setEndTime(null);
    };

    const setNextWeek = () => {
        const today = new Date();
        const currentDayOfWeek = today.getDay();
        let daysUntilNextMonday;

        if (currentDayOfWeek === 0) {
            daysUntilNextMonday = 1;
        } else {
            daysUntilNextMonday = 8 - currentDayOfWeek;
        }

        const nextWeekStart = new Date(today);
        nextWeekStart.setDate(today.getDate() + daysUntilNextMonday);
        nextWeekStart.setHours(0, 0, 0, 0);

        const nextWeekEnd = new Date(nextWeekStart);
        nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
        nextWeekEnd.setHours(23, 59, 59, 999);

        setStartDate(nextWeekStart);
        setEndDate(nextWeekEnd);
        setStartTime(null);
        setEndTime(null);
    };

    useEffect(() => {
        if (startDate) {
            checkAvailability();
        }
    }, [startDate, endDate, startTime, endTime]);

    useEffect(() => {
        setSortedItems(sortItems(availableItems, sortCriteria, sortOrder));
    }, [availableItems, sortCriteria, sortOrder]);

    const isSingleDay = startDate && (!endDate || isSameDay(startDate, endDate));

    const formatUnavailableReason = (reason) => {
        if (!reason) return '';
        if (reason.isSingleDayRent) {
            if (reason.singleDaySlot && reason.singleDaySlot.from && reason.singleDaySlot.to) {
                return `Prenotato il ${toMomentDate(reason.from).format('DD/MM/YYYY')} dalle ${reason.singleDaySlot.from} alle ${reason.singleDaySlot.to}`;
            }
            return `Prenotato il ${toMomentDate(reason.from).format('DD/MM/YYYY')}`;
        }
        if (reason.from === reason.to) {
            return `Prenotato il ${toMomentDate(reason.from).format('DD/MM/YYYY')}`;
        }
        return `Prenotato dal ${toMomentDate(reason.from).format('DD/MM/YYYY')} al ${toMomentDate(reason.to).format('DD/MM/YYYY')}`;
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: 20}}>
            <div style={{display: 'flex', flexDirection: 'column'}}>
                <MyNormalText text={'Verifica disponibilità articoli'}
                              bold={true}
                              fontSize={18}/>
                <MyNormalText text={
                    isSingleDay
                        ? 'Seleziona gli orari per vedere gli articoli disponibili e i loro slot prenotati.'
                        : 'Verranno mostrati tutti gli articoli, con indicazione di disponibilità per il range di date selezionato.'}/>
            </div>
            <div style={{display: 'flex', gap: 10, flexDirection: 'column'}}>
                <div style={{display: 'flex', gap: 10, alignItems: 'center'}}>
                    <MyNormalText text={'Data di inizio:'}/>
                    <DatePicker locale="it"
                                selected={startDate}
                                showMonthYearDropdown={false}
                                onChange={(date) => setStartDate(date)}
                                className={'custom-input'}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="Seleziona data di inizio"
                                minDate={new Date()}/>
                </div>
                <div style={{display: 'flex', gap: 10, alignItems: 'center'}}>
                    <MyNormalText text={'Data di fine (opzionale):'}/>
                    <DatePicker locale="it"
                                selected={endDate}
                                showMonthYearDropdown={false}
                                onChange={(date) => setEndDate(date)}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                className={'custom-input'}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="Seleziona data di fine"
                                minDate={startDate || new Date()}
                                isClearable/>
                </div>
                {isSingleDay && (
                    <div style={{display: 'flex', gap: 10, flexDirection: 'column'}}>
                        <div style={{display: 'flex', gap: 10, alignItems: 'center'}}>
                            <MyNormalText text={'Orario di inizio:'}/>
                            <DatePicker locale="it"
                                        selected={startTime}
                                        onChange={(time) => setStartTime(time)}
                                        showTimeSelect
                                        showTimeSelectOnly
                                        timeIntervals={15}
                                        showMonthYearDropdown={false}
                                        timeCaption="Orario"
                                        dateFormat="HH:mm"
                                        className={'custom-input'}
                                        placeholderText="Seleziona orario di inizio"
                                        disabled={!startDate}/>
                        </div>
                        <div style={{display: 'flex', gap: 10, alignItems: 'center'}}>
                            <MyNormalText text={'Orario di fine:'}/>
                            <DatePicker locale="it"
                                        selected={endTime}
                                        onChange={(time) => setEndTime(time)}
                                        showTimeSelect
                                        showTimeSelectOnly
                                        timeIntervals={15}
                                        showMonthYearDropdown={false}
                                        timeCaption="Orario"
                                        dateFormat="HH:mm"
                                        className={'custom-input'}
                                        placeholderText="Seleziona orario di fine"
                                        disabled={!startDate}
                                        minTime={startTime || new Date().setHours(0, 0)}
                                        maxTime={new Date().setHours(23, 59)}/>
                        </div>
                    </div>
                )}
            </div>
            <div style={{display: 'flex', gap: 10}}>
                <div>
                    <MainButton text={'Oggi'}
                                action={setToday}
                                backgroundColor={premiumPalette.primaryColor}
                                height={35}/>
                </div>
                <div>
                    <MainButton text={'Domani'}
                                action={setTomorrow}
                                backgroundColor={premiumPalette.primaryColor}
                                height={35}/>
                </div>
                <div>
                    <MainButton text={'Prossima settimana'}
                                action={setNextWeek}
                                backgroundColor={premiumPalette.primaryColor}
                                height={35}/>
                </div>
            </div>
            {loading && <Loader color={premiumPalette.secondaryColor} absoluteFullPage={true}/>}
            {error && <p style={{color: 'red'}}>{error}</p>}
            <div style={{display: 'flex', gap: 10}}>
                <MainButton
                    text={'Ordina per nome'}
                    action={() => handleSort('name')}
                    height={30}
                    secondaryImage={
                        sortCriteria === 'name' && sortOrder === 'asc'
                            ? require('../../../imgs/actions/arrow-down.png')
                            : require('../../../imgs/actions/arrow-up.png')
                    }
                    backgroundColor={
                        sortCriteria === 'name'
                            ? premiumPalette.secondaryColor
                            : premiumPalette.primaryColorSecondAlternative
                    }
                />
                <MainButton
                    text={'Ordina per codice'}
                    action={() => handleSort('assignedRentCode')}
                    height={30}
                    secondaryImage={
                        sortCriteria === 'assignedRentCode' && sortOrder === 'asc'
                            ? require('../../../imgs/actions/arrow-down.png')
                            : require('../../../imgs/actions/arrow-up.png')
                    }
                    backgroundColor={
                        sortCriteria === 'assignedRentCode'
                            ? premiumPalette.secondaryColor
                            : premiumPalette.primaryColorSecondAlternative
                    }
                />
                <MainButton
                    text={'Ordina per categoria'}
                    action={() => handleSort('category')}
                    height={30}
                    secondaryImage={
                        sortCriteria === 'category' && sortOrder === 'asc'
                            ? require('../../../imgs/actions/arrow-down.png')
                            : require('../../../imgs/actions/arrow-up.png')
                    }
                    backgroundColor={
                        sortCriteria === 'category'
                            ? premiumPalette.secondaryColor
                            : premiumPalette.primaryColorSecondAlternative
                    }
                />
            </div>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: 20}}>
                {sortedItems.length > 0 ? (
                    sortedItems.map((item) => (
                        <div key={item.id}>
                            <ItemBox rentUser={rentUser}
                                     premiumPalette={premiumPalette}
                                     width={280}
                                     blockOpenDetail={true}
                                     item={item}
                                     hidePrices={true}
                                     openOperations={false}/>
                            {isSingleDay && item.bookedTimeSlots && item.bookedTimeSlots.length > 0 && (
                                <div style={pageStyle.bookedTimeSlotsContainer}>
                                    <MyNormalText text="Orari prenotati:"
                                                  color={'white'}
                                                  fontSize={14}/>
                                    {item.bookedTimeSlots.map((slot, index) => (
                                        <MyNormalText key={index}
                                                      text={`- Dalle ${slot.from} alle ${slot.to}`}
                                                      color={'white'}
                                                      fontSize={14}/>
                                    ))}
                                </div>
                            )}
                            {!item.isAvailable && item.unavailableReason && (
                                <div style={pageStyle.bookedTimeSlotsContainer}>
                                    <MyNormalText text={formatUnavailableReason(item.unavailableReason)}
                                                  color={'white'}
                                                  fontSize={14}/>
                                </div>
                            )}
                        </div>
                    ))
                ) : (
                    <MyNormalText text={'Nessun articolo trovato per il noleggio selezionato.'}/>
                )}
            </div>
        </div>
    );
};

export default ItemsAvailable;

const pageStyle = {
    bookedTimeSlotsContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: 5,
        padding: 5,
        borderRadius: GLOBAL_BORDER_RADIUS,
        width: 210,
        backgroundColor: 'black'
    },
    line: {
        height: 1,
        width: '100%'
    }
}
